import React, { useMemo, useState } from 'react';
import {
  localIsoDate,
  type DocumentElement as DocumentElementType,
} from '@eaco/forms-core';
import {
  useElementValue,
  useIsEditor,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import {
  FieldHeader,
  FieldHelpText,
  FieldErrors,
  CalendarIcon,
} from './FieldParts';
import FieldPopover from './datetime/FieldPopover';
import CalendarPanel from './datetime/CalendarPanel';
import { formatShortDate, localDate, toISO, todayISO } from './datetime/dateutils';

/** Preset chip for the range footer. */
const PresetChip: React.FC<{ label: string; onClick: () => void }> = ({
  label,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-[20px] border border-[#e6e9ee] bg-[#f1f4f8] px-[11px] py-[5px] text-[11.5px] font-semibold text-[#4a5262]"
  >
    {label}
  </button>
);

interface Props {
  element: DocumentElementType;
}

/**
 * Render a yyyy-mm-dd string as a human-readable Australian-English
 * date (e.g. "5 March 2026"). Falls back to the raw string if it
 * doesn't parse, so a legacy value is never swallowed.
 */
const formatDate = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

interface DateRangeValue {
  from?: string;
  to?: string;
}

type EarliestType = 'any' | 'today_or_later' | 'specific';
type LatestType = 'any' | 'today_or_earlier' | 'specific';
type OpenEndPolicy = 'both_required' | 'open_start_ok' | 'open_end_ok';

const todayIso = (): string => localIsoDate();

/** Days between two yyyy-mm-dd strings, inclusive. Returns null if either is empty. */
const daysBetween = (from: string, to: string): number | null => {
  if (!from || !to) return null;
  const a = Date.parse(from);
  const b = Date.parse(to);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  // 1000 * 60 * 60 * 24 = ms per day; +1 for inclusive count.
  return Math.floor((b - a) / 86_400_000) + 1;
};


/** Coerce a configForm-stored numeric (always a string) to a positive int or undefined. */
const parsePositiveInt = (v: unknown): number | undefined => {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return Math.floor(n);
};

const DateRange: React.FC<Props> = ({ element }) => {
  const [rawValue, setValue] = useElementValue<DateRangeValue>(element.id);
  const isEditor = useIsEditor();
  const isReadOnly = useIsReadOnly();
  const [touched, setTouched] = useState(false);

  const data = element.data as {
    label?: string;
    subLabel?: string;
    helpText?: string;
    required?: boolean;
    hidden?: boolean;
    fromLabel?: string;
    toLabel?: string;
    earliestType?: EarliestType;
    earliestSpecificDate?: string;
    latestType?: LatestType;
    latestSpecificDate?: string;
    minLengthDays?: number | string;
    maxLengthDays?: number | string;
    openEndPolicy?: OpenEndPolicy;
  };
  const label = data?.label || 'Date range';
  const subLabel = data?.subLabel;
  const helpText = data?.helpText;
  const required = data?.required || false;
  const fromLabel = data?.fromLabel?.trim() || 'From';
  const toLabel = data?.toLabel?.trim() || 'To';
  const earliestType: EarliestType = data?.earliestType || 'any';
  const latestType: LatestType = data?.latestType || 'any';
  const minLengthDays = parsePositiveInt(data?.minLengthDays);
  const maxLengthDays = parsePositiveInt(data?.maxLengthDays);
  const openEndPolicy: OpenEndPolicy = data?.openEndPolicy || 'both_required';

  const value = useMemo<DateRangeValue>(
    () => (rawValue && typeof rawValue === 'object' ? rawValue : {}),
    [rawValue],
  );

  // Effective configured bounds — independent of what the user has currently
  // entered. We layer cross-bounds (from < to) on top of these per-input.
  const cfgEarliest = useMemo<string | undefined>(() => {
    if (earliestType === 'specific')
      return data?.earliestSpecificDate || undefined;
    if (earliestType === 'today_or_later') return todayIso();
    return undefined;
  }, [earliestType, data?.earliestSpecificDate]);

  const cfgLatest = useMemo<string | undefined>(() => {
    if (latestType === 'specific') return data?.latestSpecificDate || undefined;
    if (latestType === 'today_or_earlier') return todayIso();
    return undefined;
  }, [latestType, data?.latestSpecificDate]);

  // Range picking: first click (or after a complete range) starts a new
  // range; the next click closes it, swapping if the filler clicked before
  // the start.
  const handleDayClick = (iso: string) => {
    setTouched(true);
    const { from, to } = value;
    if (!from || (from && to)) {
      setValue({ from: iso, to: undefined });
    } else if (iso >= from) {
      setValue({ from, to: iso });
    } else {
      setValue({ from: iso, to: from });
    }
  };

  const setPreset = (from: string, to: string) => {
    setTouched(true);
    setValue({ from, to });
  };
  const presetToday = () => {
    const t = todayISO();
    setPreset(t, t);
  };
  const presetLast7 = () => {
    const n = new Date();
    setPreset(
      toISO(localDate(n.getFullYear(), n.getMonth(), n.getDate() - 6)),
      todayISO(),
    );
  };
  const presetThisMonth = () => {
    const n = new Date();
    setPreset(
      toISO(localDate(n.getFullYear(), n.getMonth(), 1)),
      toISO(localDate(n.getFullYear(), n.getMonth() + 1, 0)),
    );
  };

  // Validation — only surfaced at fill time; suppressed in editor preview.
  const errors = useMemo<string[]>(() => {
    if (isEditor || !touched) return [];
    const out: string[] = [];

    // Required + open-end policy
    if (required) {
      if (!value.from && openEndPolicy !== 'open_start_ok') {
        out.push('Start date is required');
      }
      if (!value.to && openEndPolicy !== 'open_end_ok') {
        out.push('End date is required');
      }
    }

    // Outside-bounds checks
    if (value.from && cfgEarliest && value.from < cfgEarliest) {
      out.push('Start date is outside the allowed range');
    }
    if (value.from && cfgLatest && value.from > cfgLatest) {
      out.push('Start date is outside the allowed range');
    }
    if (value.to && cfgEarliest && value.to < cfgEarliest) {
      out.push('End date is outside the allowed range');
    }
    if (value.to && cfgLatest && value.to > cfgLatest) {
      out.push('End date is outside the allowed range');
    }

    // Range length checks (only when both ends present)
    const len = daysBetween(value.from || '', value.to || '');
    if (len !== null) {
      if (minLengthDays !== undefined && len < minLengthDays) {
        out.push(
          `Range must be at least ${minLengthDays} day${
            minLengthDays === 1 ? '' : 's'
          }`,
        );
      }
      if (maxLengthDays !== undefined && len > maxLengthDays) {
        out.push(
          `Range must be at most ${maxLengthDays} day${
            maxLengthDays === 1 ? '' : 's'
          }`,
        );
      }
    }

    return Array.from(new Set(out));
  }, [
    isEditor,
    touched,
    required,
    openEndPolicy,
    value.from,
    value.to,
    cfgEarliest,
    cfgLatest,
    minLengthDays,
    maxLengthDays,
  ]);

  // Read-only (e.g. a completed result): render the chosen range as
  // static "start – end" text — no date inputs, no calendar pickers.
  if (isReadOnly) {
    const hasValue = !!(value.from || value.to);
    const rangeText = hasValue
      ? `${value.from ? formatDate(value.from) : '—'} – ${
          value.to ? formatDate(value.to) : '—'
        }`
      : '';
    return (
      <div className="space-y-2">
        <FieldHeader label={label} subLabel={subLabel} required={required} />
        <p className="text-sm">
          {hasValue ? (
            rangeText
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </p>
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FieldHeader label={label} subLabel={subLabel} required={required} />
      <FieldPopover
        id={element.id}
        icon={<CalendarIcon size={15} />}
        displayText={
          value.from
            ? `${formatShortDate(value.from)} – ${
                value.to ? formatShortDate(value.to) : '…'
              }`
            : ''
        }
        placeholder={`${fromLabel} – ${toLabel}`}
      >
        {() => (
          <CalendarPanel
            mode="range"
            rangeStart={value.from}
            rangeEnd={value.to}
            min={cfgEarliest}
            max={cfgLatest}
            onSelect={handleDayClick}
            footer={
              <div className="flex flex-wrap gap-[6px]">
                <PresetChip label="Today" onClick={presetToday} />
                <PresetChip label="Last 7 days" onClick={presetLast7} />
                <PresetChip label="This month" onClick={presetThisMonth} />
              </div>
            }
          />
        )}
      </FieldPopover>
      <FieldErrors errors={errors} />
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default DateRange;
